<template>
  <div class="ocr-report-sidebar-nav">
    <div class="ocr-report-side-tabs" role="tablist">
      <button
        v-for="tab in navTabs"
        :key="tab.value"
        type="button"
        role="tab"
        class="ocr-report-side-tab"
        :class="{ 'ocr-report-side-tab--active': navTab === tab.value }"
        :aria-selected="navTab === tab.value"
        @click="navTab = tab.value"
      >
        {{ tab.label }}
      </button>
    </div>

    <div v-if="navTab === 'tables'" class="ocr-report-sidebar-nav-panel">
      <OcrReportTableNav :model-value="selectedTableIndex" :tables="tables" @update:model-value="$emit('select-table', $event)" />
    </div>

    <div v-else-if="navTab === 'pages'" class="ocr-report-sidebar-nav-panel">
      <div class="ocr-page-nav-controls">
        <v-btn size="x-small" variant="outlined" :disabled="pageIndex <= 0" @click="goPage(pageIndex - 1)">&lt;</v-btn>
        <v-text-field
          v-model.number="pageInput"
          type="number"
          density="compact"
          variant="outlined"
          hide-details
          class="ocr-page-nav-input"
          @keyup.enter="goPage(pageInput - 1)"
        />
        <span class="ocr-page-nav-of">/ {{ pageCount || 1 }}</span>
        <v-btn
          size="x-small"
          variant="outlined"
          :disabled="pageIndex >= pageCount - 1"
          @click="goPage(pageIndex + 1)"
        >
          &gt;
        </v-btn>
      </div>
      <v-slider
        v-if="pageCount > 1"
        :model-value="pageIndex"
        :min="0"
        :max="Math.max(0, pageCount - 1)"
        :step="1"
        hide-details
        density="compact"
        class="ocr-page-nav-slider"
        @update:model-value="goPage"
      />
      <ul class="ocr-report-table-nav-list ocr-report-page-list" role="list">
        <li
          v-for="p in pageEntries"
          :key="p.page"
          class="ocr-report-table-nav-item"
          :class="{ 'ocr-report-table-nav-item--active': p.page === pageIndex }"
        >
          <button type="button" class="ocr-report-table-nav-btn" @click="selectPage(p)">
            <span class="ocr-report-table-nav-label">Page {{ p.page + 1 }}</span>
            <span
              class="ocr-report-table-nav-chip"
              :class="p.table_count ? 'ocr-report-table-nav-chip--ok' : 'ocr-report-table-nav-chip--todo'"
            >
              {{ p.table_count ? `${p.table_count} table${p.table_count === 1 ? "" : "s"}` : "No tables" }}
            </span>
          </button>
        </li>
      </ul>
    </div>

    <div v-else-if="navTab === 'mapped'" class="ocr-report-sidebar-nav-panel">
      <p v-if="!mappedTables.length" class="ocr-empty-cell">No mapped tables yet.</p>
      <ul v-else class="ocr-report-table-nav-list" role="list">
        <li v-for="(m, i) in mappedTables" :key="m.table_index + '-' + i" class="ocr-report-table-nav-item">
          <button type="button" class="ocr-report-table-nav-btn" @click="openMapped(m)">
            <span class="ocr-report-table-nav-label">{{ m.table_type }}</span>
            <span class="ocr-report-table-nav-chip ocr-report-table-nav-chip--ok">Pg {{ (m.page ?? 0) + 1 }}</span>
          </button>
        </li>
      </ul>
    </div>

    <div v-else class="ocr-report-sidebar-nav-panel">
      <div class="ocr-field">
        <label class="profile-menu-label">Search report text</label>
        <v-text-field
          v-model="searchQuery"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          @keyup.enter="runSearch"
        />
      </div>
      <v-btn size="small" class="ocr-page-nav-search-btn" variant="tonal" :loading="searchLoading" @click="runSearch">
        Search
      </v-btn>
      <p v-if="searchRan && !searchResults.length" class="ocr-empty-cell">No matches.</p>
      <ul v-else class="ocr-report-search-results" role="list">
        <li v-for="(hit, i) in searchResults" :key="i + hit.page" class="ocr-report-search-hit">
          <button type="button" class="ocr-report-table-nav-btn" @click="$emit('go-page', hit.page)">
            <span class="ocr-report-search-snippet">{{ hit.snippet }}</span>
            <span class="ocr-report-table-nav-chip">Pg {{ (hit.page ?? 0) + 1 }}</span>
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";
import OcrReportTableNav from "@/components/layout/OCR/OcrReportTableNav.vue";

export default {
  name: "OcrReportSidebarNav",
  components: { OcrReportTableNav },
  props: {
    filename: { type: String, default: null },
    tables: { type: Array, default: () => [] },
    selectedTableIndex: { type: Number, default: null },
    pageIndex: { type: Number, default: 0 },
  },
  emits: ["select-table", "go-page", "open-map-tab"],
  data() {
    return {
      navTabs: [
        { value: "tables", label: "Tables" },
        { value: "pages", label: "Pages" },
        { value: "mapped", label: "Mapped" },
        { value: "search", label: "Search" },
      ],
      navTab: "tables",
      pageCount: 0,
      pageEntries: [],
      mappedTables: [],
      pageInput: 1,
      searchQuery: "",
      searchResults: [],
      searchLoading: false,
      searchRan: false,
    };
  },
  watch: {
    filename: {
      handler() {
        this.loadNavigation();
      },
    },
    pageIndex: {
      immediate: true,
      handler(v) {
        this.pageInput = (v ?? 0) + 1;
      },
    },
    tables: {
      deep: true,
      handler() {
        this.loadNavigation();
      },
    },
  },
  mounted() {
    this.loadNavigation();
  },
  methods: {
    async loadNavigation() {
      this.mappedTables = [];
      this.pageCount = 0;
      this.pageEntries = [];
      if (!this.filename) return;
      try {
        const { data } = await ocrApi.get(
          `api/v1/reports/${encodeURIComponent(this.filename)}/navigation`,
        );
        this.pageCount = data.page_count || 0;
        this.mappedTables = data.mapped_tables || [];
        this.pageEntries = data.pages || this.pagesFromTables(this.pageCount);
      } catch {
        this.pageCount = Math.max(...(this.tables.map((t) => t.page ?? 0)), 0) + 1;
        this.pageEntries = this.pagesFromTables(this.pageCount);
      }
    },
    pagesFromTables(pageCount) {
      const count = Math.max(pageCount || 0, 1);
      const byPage = {};
      (this.tables || []).forEach((t) => {
        const page = t.page ?? 0;
        byPage[page] = (byPage[page] || 0) + 1;
      });
      return Array.from({ length: count }, (_, page) => ({
        page,
        table_count: byPage[page] || 0,
        table_indexes: (this.tables || []).filter((t) => t.page === page).map((t) => t.index),
      }));
    },
    selectPage(entry) {
      this.goPage(entry.page);
      const first = (entry.table_indexes || [])[0];
      if (first != null) this.$emit("select-table", first);
    },
    goPage(idx) {
      const max = Math.max(0, (this.pageCount || 1) - 1);
      const clamped = Math.min(Math.max(0, idx), max);
      this.pageInput = clamped + 1;
      this.$emit("go-page", clamped);
    },
    openMapped(m) {
      this.$emit("go-page", m.page ?? 0);
      if (m.table_index != null) this.$emit("select-table", m.table_index);
      this.$emit("open-map-tab");
    },
    async runSearch() {
      if (!this.filename || !(this.searchQuery || "").trim()) {
        this.searchResults = [];
        this.searchRan = false;
        return;
      }
      this.searchLoading = true;
      try {
        const q = encodeURIComponent(this.searchQuery.trim());
        const { data } = await ocrApi.get(
          `api/v1/reports/${encodeURIComponent(this.filename)}/navigation?search=${q}`,
        );
        this.searchResults = data.search_results || [];
        this.searchRan = true;
      } catch (e) {
        this.searchResults = [];
        this.searchRan = true;
        console.warn(formatOcrApiError(e, "Search failed."));
      } finally {
        this.searchLoading = false;
      }
    },
  },
};
</script>
